/**
 * Common utility for seamless API data updates in SlideOutPanel
 * Provides immediate UI feedback while syncing with API in background
 */

export interface ApiDataUpdaterOptions {
  setApiData: (updater: (prevData: any) => any) => void;
  apiUpdateFunction?: (...args: any[]) => Promise<any>;
  onSuccess?: (result: any) => void;
  /**
   * Optional error hook. For most updater methods, errors are caught here and not re-thrown.
   * Incremental additional-info helpers (`addAdditionalInfoLink`, `updateAdditionalInfoLink`,
   * `deleteAdditionalInfoLink`) invoke this and then re-throw so callers using try/catch or
   * `.catch()` can still handle failures (e.g. user-facing toasts).
   */
  onError?: (error: any) => void;
  issueId?: string;
  momentName?: string;
  momentType?: string;
}

/**
 * Creates a seamless API data updater that immediately updates local state
 * and then syncs with the API in the background
 */
export const createApiDataUpdater = (options: ApiDataUpdaterOptions) => {
  const {
    setApiData,
    apiUpdateFunction,
    onSuccess,
    onError,
    issueId,
    momentName = 'Unknown',
    momentType
  } = options;

  return {
    /**
     * Update primary contacts with immediate UI feedback
     */
    updatePrimaryContacts: async (
      updatedContacts: any[],
      convertContactsToApiFormat: (contacts: any[], role?: string) => any,
      updatedRole?: string,
      customApiFunction?: (contacts: any[], role?: string) => Promise<any>
    ) => {
      setApiData(prevApiData => {
        if (!prevApiData) return prevApiData;

        const updatedApiData = { ...prevApiData };
        if (!updatedApiData.primaryContacts) {
          updatedApiData.primaryContacts = {
            productMarketingManagers: [],
            programManagers: [],
            productManagers: []
          };
        }

        const apiFormat = convertContactsToApiFormat(
          updatedContacts,
          updatedRole
        );
        updatedApiData.primaryContacts = {
          ...updatedApiData.primaryContacts,
          ...apiFormat
        };

        return updatedApiData;
      });

      try {
        if (customApiFunction) {
          const result = await customApiFunction(updatedContacts, updatedRole);
          onSuccess?.(result);
        } else if (apiUpdateFunction) {
          const apiFormat = convertContactsToApiFormat(
            updatedContacts,
            updatedRole
          );
          const result = await apiUpdateFunction({
            primaryContacts: apiFormat
          });
          onSuccess?.(result);
        }
      } catch (error) {
        onError?.(error);
      }
    },

    /**
     * Update primary contacts for a specific phase with immediate UI feedback
     */
    updatePhasedPrimaryContacts: async (
      phaseId: string,
      updatedContacts: any[],
      convertContactsToApiFormat: (contacts: any[], role?: string) => any,
      updatedRole?: string,
      customApiFunction?: (
        phaseId: string,
        contacts: any[],
        role?: string
      ) => Promise<any>
    ) => {
      setApiData(prevApiData => {
        if (!prevApiData?.releasePhases) return prevApiData;

        const updatedApiData = { ...prevApiData };
        const apiFormat = convertContactsToApiFormat(
          updatedContacts,
          updatedRole
        );

        updatedApiData.releasePhases = updatedApiData.releasePhases.map(
          (phase: any) => {
            if (phase.id === phaseId) {
              return {
                ...phase,
                primaryContacts: {
                  ...phase.primaryContacts,
                  ...apiFormat
                }
              };
            }
            return phase;
          }
        );

        return updatedApiData;
      });

      try {
        if (customApiFunction) {
          const result = await customApiFunction(
            phaseId,
            updatedContacts,
            updatedRole
          );
          onSuccess?.(result);
        } else if (apiUpdateFunction) {
          let updatePayload: any;

          setApiData(prevApiData => {
            if (!prevApiData?.releasePhases) return prevApiData;

            const apiFormat = convertContactsToApiFormat(
              updatedContacts,
              updatedRole
            );
            updatePayload = {
              releasePhases: prevApiData.releasePhases.map((phase: any) => {
                if (phase.id === phaseId) {
                  return {
                    ...phase,
                    primaryContacts: {
                      ...phase.primaryContacts,
                      ...apiFormat
                    }
                  };
                }
                return phase;
              })
            };

            return prevApiData;
          });

          if (updatePayload) {
            const result = await apiUpdateFunction(updatePayload);
            onSuccess?.(result);
          }
        }
      } catch (error) {
        onError?.(error);
      }
    },

    /**
     * Update additional info links with immediate UI feedback
     */
    updateAdditionalInfo: async (updatedLinks: any[]) => {
      setApiData(prevApiData => {
        if (!prevApiData) return prevApiData;

        const updatedApiData = { ...prevApiData };
        updatedApiData.additionalInfo = updatedLinks;
        return updatedApiData;
      });

      if (apiUpdateFunction) {
        try {
          const result = await apiUpdateFunction({
            additionalInfo: updatedLinks
          });
          onSuccess?.(result);
        } catch (error) {
          onError?.(error);
        }
      }
    },

    /**
     * Add a single additional info link (incremental update)
     * Sends only the new link as a single entry in additionalInfo array
     *
     * On API failure: calls `onError`, then **re-throws** the error (unlike bulk updaters in
     * this module). Callers should use try/catch or chain `.catch()` to avoid unhandled
     * rejections and to surface errors to the user.
     */
    addAdditionalInfoLink: async (newLink: { title: string; url: string }) => {
      let previousLinks: any[] = [];
      const linkWithId = {
        id: String(Date.now()),
        title: newLink.title,
        url: newLink.url
      };

      setApiData(prevApiData => {
        if (!prevApiData) return prevApiData;

        const updatedApiData = { ...prevApiData };
        const currentLinks = Array.isArray(updatedApiData.additionalInfo)
          ? updatedApiData.additionalInfo
          : [];
        previousLinks = currentLinks;
        updatedApiData.additionalInfo = [...currentLinks, linkWithId];
        return updatedApiData;
      });

      if (apiUpdateFunction) {
        try {
          const result = await apiUpdateFunction({
            additionalInfo: [linkWithId]
          });
          onSuccess?.(result);
          return result;
        } catch (error) {
          onError?.(error);
          throw error;
        }
      }
      return undefined;
    },

    /**
     * Delete a single additional info link using _delete flag
     * @param linkId - The ID of the link to delete
     *
     * On API failure: calls `onError`, then **re-throws** the error. See `addAdditionalInfoLink`.
     */
    deleteAdditionalInfoLink: async (linkId: string) => {
      let deletedLink: any;
      setApiData(prevApiData => {
        if (!prevApiData) return prevApiData;

        const updatedApiData = { ...prevApiData };
        const currentLinks = Array.isArray(updatedApiData.additionalInfo)
          ? updatedApiData.additionalInfo
          : [];

        deletedLink = currentLinks.find(link => link.id === linkId);
        updatedApiData.additionalInfo = currentLinks.filter(
          link => link.id !== linkId
        );
        return updatedApiData;
      });

      if (apiUpdateFunction) {
        try {
          const result = await apiUpdateFunction({
            additionalInfo: [{ id: linkId, _delete: true }]
          });
          onSuccess?.(result);
          return result;
        } catch (error) {
          onError?.(error);
          throw error;
        }
      }
      return undefined;
    },

    /**
     * Update a single additional info link (incremental update)
     * Sends only the updated link as a single entry in additionalInfo array
     *
     * On API failure: calls `onError`, then **re-throws** the error. See `addAdditionalInfoLink`.
     */
    updateAdditionalInfoLink: async (updatedLink: {
      id: string;
      title: string;
      url: string;
    }) => {
      let previousLink: any;
      setApiData(prevApiData => {
        if (!prevApiData) return prevApiData;

        const updatedApiData = { ...prevApiData };
        const currentLinks = Array.isArray(updatedApiData.additionalInfo)
          ? updatedApiData.additionalInfo
          : [];

        previousLink = currentLinks.find(link => link.id === updatedLink.id);
        updatedApiData.additionalInfo = currentLinks.map(link =>
          link.id === updatedLink.id ? updatedLink : link
        );
        return updatedApiData;
      });

      if (apiUpdateFunction) {
        try {
          const result = await apiUpdateFunction({
            additionalInfo: [updatedLink]
          });
          onSuccess?.(result);
          return result;
        } catch (error) {
          onError?.(error);
          throw error;
        }
      }
      return undefined;
    },

    /**
     * Update overview data with immediate UI feedback
     */
    updateOverview: async (updatedOverview: any) => {
      setApiData(prevApiData => {
        if (!prevApiData) return prevApiData;

        const updatedApiData = { ...prevApiData };
        updatedApiData.overview = {
          ...updatedApiData.overview,
          ...updatedOverview
        };
        return updatedApiData;
      });

      if (apiUpdateFunction) {
        try {
          const result = await apiUpdateFunction({ overview: updatedOverview });
          onSuccess?.(result);
        } catch (error) {
          onError?.(error);
        }
      }
    },

    /**
     * Update decisions with immediate UI feedback
     */
    updateDecisions: async (updatedDecisions: any[]) => {
      setApiData(prevApiData => {
        if (!prevApiData) return prevApiData;

        const updatedApiData = { ...prevApiData };
        updatedApiData.decisions = updatedDecisions;
        return updatedApiData;
      });

      if (apiUpdateFunction) {
        try {
          const result = await apiUpdateFunction({
            decisions: updatedDecisions
          });
          onSuccess?.(result);
        } catch (error) {
          onError?.(error);
        }
      }
    },

    /**
     * Update DACI contacts with immediate UI feedback
     */
    updateDaciContacts: async (updatedDaciContacts: any[]) => {
      setApiData(prevApiData => {
        if (!prevApiData) return prevApiData;

        const updatedApiData = { ...prevApiData };
        updatedApiData.daciContacts = updatedDaciContacts;
        return updatedApiData;
      });

      if (apiUpdateFunction) {
        try {
          const result = await apiUpdateFunction({
            daciContacts: updatedDaciContacts
          });
          onSuccess?.(result);
        } catch (error) {
          onError?.(error);
        }
      }
    },

    /**
     * Generic field updater for any API data field
     */
    updateField: async (fieldPath: string, updatedValue: any) => {
      setApiData(prevApiData => {
        if (!prevApiData) return prevApiData;

        const updatedApiData = { ...prevApiData };

        const pathParts = fieldPath.split('.');
        let current = updatedApiData;

        for (let i = 0; i < pathParts.length - 1; i++) {
          if (!current[pathParts[i]]) {
            current[pathParts[i]] = {};
          }
          current = current[pathParts[i]];
        }

        current[pathParts[pathParts.length - 1]] = updatedValue;

        return updatedApiData;
      });

      if (apiUpdateFunction) {
        try {
          const updateData: Record<string, unknown> = {};
          const pathParts = fieldPath.split('.');
          let current: Record<string, unknown> = updateData;

          for (let i = 0; i < pathParts.length - 1; i++) {
            current[pathParts[i]] = {};
            current = current[pathParts[i]] as Record<string, unknown>;
          }
          current[pathParts[pathParts.length - 1]] = updatedValue;

          const result = await apiUpdateFunction(updateData);
          onSuccess?.(result);
        } catch (error) {
          onError?.(error);
        }
      }
    }
  };
};

/**
 * Hook for using the API data updater in components
 */
export const useApiDataUpdater = (options: ApiDataUpdaterOptions) => {
  return createApiDataUpdater(options);
};
